import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useModal } from './ModalContext';
import { useLayout } from './LayoutContext';

const Modal: React.FC = () => {
  const { modals, closeModal } = useModal();
  const layoutContext = useLayout();
  const modalRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [sidebarState, setSidebarState] = useState({ collapsed: false, isMobile: false });
  const observerRef = useRef<MutationObserver | null>(null);

  // Detect sidebar state from DOM or context
  const getSidebarState = () => {
    // Try to use context first
    if (layoutContext?.sidebarCollapsed !== undefined && layoutContext?.isMobile !== undefined) {
      return {
        collapsed: layoutContext.sidebarCollapsed,
        isMobile: layoutContext.isMobile,
      };
    }

    // Fall back to DOM detection
    const sidebar = document.getElementById('sidebar');
    if (!sidebar) return { collapsed: false, isMobile: false };

    const sidebarClasses = sidebar.className || '';
    const isCollapsed = sidebarClasses.includes('w-20');
    const isMobile = sidebarClasses.includes('translate-x-0') && !sidebarClasses.includes('md:translate-x-0');

    return { collapsed: isCollapsed, isMobile };
  };

  // Update sidebar state when modals change or context changes
  useEffect(() => {
    const updateSidebarState = () => {
      setSidebarState(getSidebarState());
    };

    updateSidebarState();

    // Set up MutationObserver to watch for sidebar class changes
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      observerRef.current = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            updateSidebarState();
          }
        });
      });

      observerRef.current.observe(sidebar, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Also listen for window resize events
    const handleResize = () => updateSidebarState();
    window.addEventListener('resize', handleResize);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [modals, layoutContext]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && modals.length > 0) {
        const lastModal = modals[modals.length - 1];
        if (lastModal && lastModal.options?.closable !== false) {
          closeModal(lastModal.id);
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [modals, closeModal]);

  // Handle backdrop click
  const handleBackdropClick = (modalId: string, event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      const modal = modals.find(m => m.id === modalId);
      if (modal?.options?.closable !== false) {
        closeModal(modalId);
      }
    }
  };

  // Focus management
  useEffect(() => {
    if (modals.length > 0) {
      const lastModal = modals[modals.length - 1];
      if (!lastModal) return;
      const modalElement = modalRefs.current.get(lastModal.id);
      if (modalElement) {
        const focusableElements = modalElement.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstFocusableElement = focusableElements[0] as HTMLElement;
        if (firstFocusableElement) {
          firstFocusableElement.focus();
        }
      }
    }
  }, [modals]);

  if (modals.length === 0) return null;

  return (
    <>
      {modals.map((modal, index) => {
        const {
          size = 'medium',
          closable = true,
          backdrop = 'blur',
          position = 'center',
          animation = 'fade',
          zIndex = 1000,
          disableSidebarOffset = false,
        } = modal.options || {};

        const { collapsed, isMobile } = sidebarState;

        const getSizeClasses = () => {
          switch (size) {
            case 'small':
              return 'max-w-lg w-full';
            case 'fullscreen':
              return 'max-w-full max-h-full w-full';
            default:
              return 'max-w-2xl w-full';
          }
        };

        const getBackdropClasses = () => {
          switch (backdrop) {
            case 'dark':
              return 'bg-slate-900/75';
            case 'none':
              return 'bg-transparent';
            default:
              return 'bg-slate-900/50 backdrop-blur-sm';
          }
        };

        const getPositionClasses = () => {
          switch (position) {
            case 'top':
              return 'items-start pt-20';
            case 'bottom':
              return 'items-end pb-20';
            default:
              return 'items-center';
          }
        };

        const getAnimationClasses = () => {
          switch (animation) {
            case 'slide':
              return 'animate-[slideIn_0.3s_ease-out]';
            case 'scale':
              return 'animate-[scaleIn_0.3s_ease-out]';
            case 'bounce':
              return 'animate-[bounceIn_0.5s_ease-out]';
            default:
              return 'animate-[fadeIn_0.3s_ease-out]';
          }
        };

        const ModalComponent = modal.component;

        return (
          <div
            key={modal.id}
            className={`fixed inset-0 ${getBackdropClasses()} overflow-y-auto h-full transition-all duration-300`}
            style={{
              zIndex: zIndex + index,
              ...(disableSidebarOffset ? {} : {
                left: isMobile ? '0px' : collapsed ? '5rem' : '16rem',
                width: isMobile ? '100vw' : collapsed ? 'calc(100vw - 5rem)' : 'calc(100vw - 16rem)',
              }),
            }}
            onClick={(e) => handleBackdropClick(modal.id, e)}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`modal-title-${modal.id}`}
          >
            <div className={`relative min-h-full flex flex-col items-center justify-center p-12 ${getPositionClasses()}`}>

              <div
                ref={(el) => {
                  if (el) {
                    modalRefs.current.set(modal.id, el);
                  } else {
                    modalRefs.current.delete(modal.id);
                  }
                }}
                className={`${ModalComponent.name === 'ConfirmModal' ? '' : 'bg-white'} rounded-2xl shadow-2xl  ${getSizeClasses()} ${getAnimationClasses()}`}
              >
                {closable && (
                  <button
                    onClick={() => closeModal(modal.id)}
                    className="absolute top-4 right-4 z-10 p-2 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                    aria-label="Close modal"
                  >
                    <X className="text-slate-600" size={20} />
                  </button>
                )}

                <ModalComponent
                  {...modal.props}
                  modalId={modal.id}
                  onClose={() => closeModal(modal.id)}
                />
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default Modal;
